import {
  computeGatepassExpiryDate,
  gatepassExpiryExceedsScValidity,
} from "@features/gatepass/schema/gatepassScValidity.utils";
import { t, type TFunction } from "i18next";
import { z } from "zod";
import {
  emiratesIdNumberSchema,
  passportNumberSchema,
  requiredLettersWithSpaces,
} from "./normalGatepassFormSchema";

const required = (max: number, label = t("form-validation.field")) =>
  z
    .string()
    .min(1, t("form-validation.required", { label }))
    .max(max, t("form-validation.max-length", { label, max }));

const optional = (max: number, label = t("form-validation.field")) =>
  z.string().max(max, t("form-validation.max-length", { label, max })).optional();

const requiredPhone = (max: number, label = t("gatepass.phone-number")) =>
  z
    .string()
    .min(1, t("form-validation.required", { label }))
    .regex(/^\+[1-9]\d{6,14}$/, {
      message: t("form-validation.telephone", { label }),
    })
    .max(max, t("form-validation.max-length", { label, max }));

const requiredAlphaNumericWithSpaces = (max: number, label = t("form-validation.field")) =>
  z
    .string()
    .min(1, t("form-validation.required", { label }))
    .max(max, t("form-validation.max-length", { label, max }))
    .regex(/^[A-Za-z0-9 ]+$/, {
      message: t("form-validation.regex-no-special-chars", { label }),
    });

const optionalAlphaNumericWithSpaces = (max: number, label = t("form-validation.field")) =>
  z.preprocess(
    (val) => (val === null || val === "" ? undefined : val),
    z
      .string()
      .max(max, t("form-validation.max-length", { label, max }))
      .regex(/^[A-Za-z0-9 ]+$/, {
        message: t("form-validation.regex-no-special-chars", { label }),
      })
      .optional(),
  );

const requiredDate = (label: string) =>
  z.union([
    z.date({
      required_error: t("form-validation.required", { label }),
      invalid_type_error: t("form-validation.required", { label }),
    }),
    z.string().min(1, t("form-validation.required", { label })),
  ]);

const optionalDateField = (invalidTypeMessage: string) =>
  z.preprocess(
    (val) => (val === null || val === "" ? undefined : val),
    z
      .union([
        z.date({
          invalid_type_error: invalidTypeMessage,
        }),
        z.string(),
      ])
      .optional(),
  );

const toStartOfDay = (value: Date): Date => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const parseDateValue = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

const parseNumeric = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const decimalPlaces = (value: unknown): number => {
  const raw = typeof value === "number" ? String(value) : String(value ?? "").trim();
  if (!raw.includes(".")) return 0;
  return raw.split(".")[1]?.length ?? 0;
};

const TRANSLATION_KEY = "gatepass";
export const TRUCK_JOB_NUMBER_MAX_LENGTH = 18;

const truckVehicleSchema = (tFn: TFunction) =>
  z.object({
    registrationEmirate: required(200, tFn(`${TRANSLATION_KEY}.registration-emirate`)),
    vehicleType: required(200, tFn(`${TRANSLATION_KEY}.vehicle-type`)),
    vehicleColour: requiredLettersWithSpaces(50, tFn(`${TRANSLATION_KEY}.vehicle-colour`)),
    category: required(200, tFn(`${TRANSLATION_KEY}.category`)),
    plateNumber: requiredAlphaNumericWithSpaces(50, tFn(`${TRANSLATION_KEY}.vehicle-plate-number`)),
    drivers: z
      .array(
        z.object({
          driverLicenseNumber: requiredAlphaNumericWithSpaces(
            50,
            tFn("security-clearance.driving-license-number"),
          ),
          fullName: required(200, tFn(`${TRANSLATION_KEY}.fullName-en`)),
          passportNumber: z.string().max(50).optional(),
          emiratesIdNumber: z.string().max(50).optional(),
          mobileNumber: required(50, tFn(`${TRANSLATION_KEY}.phone-number`)),
          securityClearanceId: z.string().optional(),
          securityClearanceValidity: z.string().optional(),
          securityClearanceFott: z.string().optional(),
        }),
      )
      .min(
        1,
        tFn("form-validation.required", {
          label: tFn(`${TRANSLATION_KEY}.driver-details`, { defaultValue: "Driver Details" }),
        }),
      ),
  });

export const getTruckGatepassFormSchema = (
  tFn: TFunction,
  options?: { isIndividualRole?: boolean },
) => {
  const isIndividualRole = options?.isIndividualRole === true;

  return z
    .object({
      SCStatus: z.string(),
      securityClearanceSerial: z.string().optional(),
      securityClearanceId: z.string().optional(),
      securityClearanceValidity: z.string().optional(),
      securityClearanceFott: z.string().optional(),
      gatepassCategory: z.string(),
      // Visitor Details
      passportNumber: passportNumberSchema(15, tFn(`${TRANSLATION_KEY}.passport-number`)),
      emiratesIdNumber: emiratesIdNumberSchema(tFn(`${TRANSLATION_KEY}.emirates-id-number`)),
      fullName: required(200, tFn(`${TRANSLATION_KEY}.fullName-en`)),
      nationality: required(200, tFn(`${TRANSLATION_KEY}.nationality`)),
      passportNumberExpirationDate: optional(
        20,
        tFn(`${TRANSLATION_KEY}.passport-number-expiration-date`),
      ),
      driverLicenseNumber: z.string().optional(),
      phoneNumber: requiredPhone(200, tFn(`${TRANSLATION_KEY}.phone-number`)),
      sponsorCompany: required(200, tFn(`${TRANSLATION_KEY}.sponsor-company`)),
      expectedEntryDate: requiredDate(tFn(`${TRANSLATION_KEY}.expected-entry-date`)),
      port: required(200, tFn(`${TRANSLATION_KEY}.port`)),
      gate: required(200, tFn(`${TRANSLATION_KEY}.gate-selection`)),
      visitorCompanyEnglish: required(200, tFn(`${TRANSLATION_KEY}.visitor-company-name-en`)),
      visitorCompanyArabic: required(200, tFn(`${TRANSLATION_KEY}.visitor-company-name-ar`)),
      reference: optional(200, tFn(`${TRANSLATION_KEY}.reference`)),
      // Gatepass Details
      truckType: z.string().max(
        20,
        tFn("form-validation.max-length", {
          label: tFn(`${TRANSLATION_KEY}.truck-cargo-mode`),
          max: 20,
        }),
      ),
      gatepassType: z.string().max(
        50,
        tFn("form-validation.max-length", {
          label: tFn(`${TRANSLATION_KEY}.type-of-gatepass`),
          max: 50,
        }),
      ),
      vip: z.boolean().nullable(),
      requestFor: z.string().max(
        20,
        tFn("form-validation.max-length", {
          label: tFn(`${TRANSLATION_KEY}.request-for`),
          max: 20,
        }),
      ),
      cargoDestination: z.string().max(
        50,
        tFn("form-validation.max-length", {
          label: tFn(`${TRANSLATION_KEY}.cargo-destination`),
          max: 50,
        }),
      ),
      gatepassDuration: required(50, tFn(`${TRANSLATION_KEY}.gatepass-duration`)),
      permanentDuration: z.string().optional(),
      entryType: z.string().max(
        50,
        tFn("form-validation.max-length", {
          label: tFn(`${TRANSLATION_KEY}.entry-type`),
          max: 50,
        }),
      ),
      FOTTEntry: z.boolean().nullable(),
      isFottDisabled: z.boolean(),
      destinationVcn: z.string().optional(),
      vesselName: z.string().max(200).optional(),
      serviceNumber: z.union([z.string(), z.number()]).optional(),
      cargoHandlingParty: z.string().optional(),
      deliverTo: optionalAlphaNumericWithSpaces(100, tFn(`${TRANSLATION_KEY}.deliver-to`)),
      jobNumber: z.union([z.string(), z.number()]).optional(),
      jobDescription: z
        .string()
        .max(
          500,
          tFn("form-validation.max-length", {
            label: tFn(`${TRANSLATION_KEY}.job-description`),
            max: 500,
          }),
        )
        .optional(),
      jobStartDate: optionalDateField(tFn("form-validation.expected-date")),
      jobEndDate: optionalDateField(tFn("form-validation.expected-date")),
      cargos: z.array(
        z.object({
          cargoType: z.string(),
          cargo: z.string(),
          hazardCargo: z.boolean(),
          quantity: z.union([z.string(), z.number()]),
          unitOfMeasurement: z.string(),
        }),
      ),
      deliveryNotesDocuments: z
        .array(z.any())
        .min(
          1,
          tFn("form-validation.required", {
            label: tFn(`${TRANSLATION_KEY}.delivery-notes-documents`, {
              defaultValue: "Delivery Notes Documents",
            }),
          }),
        )
        .max(
          5,
          tFn("form-validation.max-length", {
            label: tFn(`${TRANSLATION_KEY}.delivery-notes-documents`, {
              defaultValue: "Delivery Notes Documents",
            }),
            max: 5,
          }),
        ),
      // Vehicle Details
      vehicles: z.array(truckVehicleSchema(tFn)).min(
        1,
        tFn("form-validation.required", {
          label: tFn(`${TRANSLATION_KEY}.vehicle-details`),
        }),
      ),
      groupMembers: z.array(z.any()),
      // Photo
      photoUrl: optional(200, tFn(`${TRANSLATION_KEY}.photoUrl`)),
    })
    .superRefine((data, ctx) => {
      const gatepassExpiryDate = computeGatepassExpiryDate(
        data.expectedEntryDate,
        data.gatepassDuration,
        data.permanentDuration,
      );

      if (gatepassExpiryExceedsScValidity(gatepassExpiryDate, data.securityClearanceValidity)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn(`${TRANSLATION_KEY}.gatepass-exceeds-security-clearance-expiry`),
          path: ["securityClearanceValidity"],
        });
      }

      const normalizedGatepassDuration = data.gatepassDuration?.trim().toLowerCase();
      if (normalizedGatepassDuration && !["daily", "weekly"].includes(normalizedGatepassDuration)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn("form-validation.invalid", {
            label: tFn(`${TRANSLATION_KEY}.gatepass-duration`),
          }),
          path: ["gatepassDuration"],
        });
      }

      const hasPassportNumber = Boolean(data.passportNumber?.trim());
      const hasEmiratesIdNumber = Boolean(data.emiratesIdNumber?.trim());

      if (!hasPassportNumber && !hasEmiratesIdNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn("form-validation.required", {
            label: tFn(`${TRANSLATION_KEY}.passport-number`),
          }),
          path: ["passportNumber"],
        });

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn("form-validation.required", {
            label: tFn(`${TRANSLATION_KEY}.emirates-id-number`),
          }),
          path: ["emiratesIdNumber"],
        });
      }

      if ((hasPassportNumber || hasEmiratesIdNumber) && !data.securityClearanceId?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn(`${TRANSLATION_KEY}.notifications.security-clearance-not-found`),
          path: ["securityClearanceId"],
        });
      }

      if (!isIndividualRole && !data.requestFor?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn("form-validation.required", {
            label: tFn(`${TRANSLATION_KEY}.request-for`),
          }),
          path: ["requestFor"],
        });
      }

      if (!data.cargoDestination?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn("form-validation.required", {
            label: tFn(`${TRANSLATION_KEY}.cargo-destination`),
          }),
          path: ["cargoDestination"],
        });
      }

      if (!isIndividualRole && !data.entryType?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn("form-validation.required", {
            label: tFn(`${TRANSLATION_KEY}.entry-type`),
          }),
          path: ["entryType"],
        });
      }

      if (!Array.isArray(data.cargos) || data.cargos.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn("form-validation.required", {
            label: tFn(`${TRANSLATION_KEY}.cargo-details`),
          }),
          path: ["cargos"],
        });
      }

      const isActiveHazardCargo = (cargo: (typeof data.cargos)[number]) =>
        cargo.hazardCargo === true && Boolean(cargo.cargo?.trim());

      const hasHazardCargo = data.cargos.some(isActiveHazardCargo);
      if (hasHazardCargo && data.cargos.length > 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn(`${TRANSLATION_KEY}.hazard-cargo-multiple-types-not-allowed`),
          path: ["cargos"],
        });
      }

      if (hasHazardCargo && Array.isArray(data.groupMembers) && data.groupMembers.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn(`${TRANSLATION_KEY}.hazard-cargo-warning`),
          path: ["cargos"],
        });
      }

      if (hasHazardCargo) {
        if (Array.isArray(data.vehicles) && data.vehicles.length > 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn(`${TRANSLATION_KEY}.hazard-cargo-warning`),
            path: ["vehicles"],
          });
        }

        const totalDrivers = (data.vehicles ?? []).reduce(
          (count, vehicle) => count + (Array.isArray(vehicle.drivers) ? vehicle.drivers.length : 0),
          0,
        );

        if (totalDrivers > 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn(`${TRANSLATION_KEY}.hazard-cargo-warning`),
            path: ["vehicles", 0, "drivers"],
          });
        }
      }

      const truckType = String(data.truckType ?? "TRUCK")
        .trim()
        .toUpperCase();

      if (
        !isIndividualRole &&
        ["TRUCK", "CARGO"].includes(truckType) &&
        data.requestFor?.trim().toLowerCase() !== "entry"
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn("form-validation.required", {
            label: tFn(`${TRANSLATION_KEY}.request-for`),
          }),
          path: ["requestFor"],
        });
      }

      const hasJobStartDateValue = Boolean(String(data.jobStartDate ?? "").trim());
      const hasJobEndDateValue = Boolean(String(data.jobEndDate ?? "").trim());

      if (truckType === "TRUCK") {
        if (!String(data.jobNumber ?? "").trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.required", {
              label: tFn(`${TRANSLATION_KEY}.job-number`),
            }),
            path: ["jobNumber"],
          });
        }

        if (!String(data.jobDescription ?? "").trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.required", {
              label: tFn(`${TRANSLATION_KEY}.job-description`),
            }),
            path: ["jobDescription"],
          });
        }

        if (!hasJobStartDateValue) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.required", {
              label: tFn(`${TRANSLATION_KEY}.job-start-date`),
            }),
            path: ["jobStartDate"],
          });
        }

        if (!hasJobEndDateValue) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.required", {
              label: tFn(`${TRANSLATION_KEY}.job-end-date`),
            }),
            path: ["jobEndDate"],
          });
        }
      }

      const hasDestinationVcn = Boolean(data.destinationVcn?.trim());
      const hasVesselName = Boolean(data.vesselName?.trim());
      const hasServiceNumberInput = Boolean(String(data.serviceNumber ?? "").trim());
      const hasJobNumberInput = Boolean(String(data.jobNumber ?? "").trim());
      const hasTruckJobIdentifier = hasDestinationVcn || hasServiceNumberInput || hasJobNumberInput;
      const hasRequiredTruckJobIdentifier = hasHazardCargo
        ? hasDestinationVcn || hasServiceNumberInput
        : hasTruckJobIdentifier;
      const cargoDestination = String(data.cargoDestination ?? "")
        .trim()
        .toUpperCase();
      const isVesselCargoDestination = cargoDestination === "VESSEL";
      const isPortCargoDestination = cargoDestination === "PORT";

      if (data.truckType?.trim().toUpperCase() === "CARGO" && isPortCargoDestination) {
        if (!data.deliverTo?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.required", {
              label: tFn(`${TRANSLATION_KEY}.deliver-to`),
            }),
            path: ["deliverTo"],
          });
        }
      }

      const hasJobDescription = Boolean(data.jobDescription?.trim());
      const hasCargoDestinationJobRules = isVesselCargoDestination || isPortCargoDestination;

      if (hasDestinationVcn && !hasVesselName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn("form-validation.required", {
            label: tFn(`${TRANSLATION_KEY}.vessel-name`),
          }),
          path: ["vesselName"],
        });
      }

      if (truckType === "TRUCK" && !hasRequiredTruckJobIdentifier && !hasCargoDestinationJobRules) {
        const requiredTruckJobFields = hasHazardCargo
          ? ([
              ["destinationVcn", tFn(`${TRANSLATION_KEY}.destination-vcn`)],
              ["serviceNumber", tFn(`${TRANSLATION_KEY}.service-number`)],
            ] as const)
          : ([
              ["destinationVcn", tFn(`${TRANSLATION_KEY}.destination-vcn`)],
              ["serviceNumber", tFn(`${TRANSLATION_KEY}.service-number`)],
              ["jobNumber", tFn(`${TRANSLATION_KEY}.job-number`)],
            ] as const);

        requiredTruckJobFields.forEach(([field, label]) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.required", { label }),
            path: [field],
          });
        });
      }

      if (isVesselCargoDestination && !hasDestinationVcn && !hasServiceNumberInput) {
        (
          [
            ["destinationVcn", tFn(`${TRANSLATION_KEY}.destination-vcn`)],
            ["serviceNumber", tFn(`${TRANSLATION_KEY}.service-number`)],
          ] as const
        ).forEach(([field, label]) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.required", { label }),
            path: [field],
          });
        });
      }

      if (isPortCargoDestination && !hasHazardCargo) {
        if (!hasJobNumberInput) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.required", {
              label: tFn(`${TRANSLATION_KEY}.job-number`),
            }),
            path: ["jobNumber"],
          });
        }

        if (!hasJobDescription) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.required", {
              label: tFn(`${TRANSLATION_KEY}.job-description`),
            }),
            path: ["jobDescription"],
          });
        }
      }

      const serviceNumberValue = data.serviceNumber;
      if (
        serviceNumberValue !== undefined &&
        serviceNumberValue !== null &&
        String(serviceNumberValue).trim() !== ""
      ) {
        const serviceNumber = parseNumeric(serviceNumberValue);
        if (serviceNumber === null || serviceNumber <= 0 || !Number.isInteger(serviceNumber)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.invalid", {
              label: tFn(`${TRANSLATION_KEY}.service-number`),
            }),
            path: ["serviceNumber"],
          });
        }
      }

      const jobNumberValue = data.jobNumber;
      if (
        jobNumberValue !== undefined &&
        jobNumberValue !== null &&
        String(jobNumberValue).trim() !== ""
      ) {
        const jobNumber = parseNumeric(jobNumberValue);
        if (String(jobNumberValue).length > TRUCK_JOB_NUMBER_MAX_LENGTH) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.max-length", {
              label: tFn(`${TRANSLATION_KEY}.job-number`),
              max: TRUCK_JOB_NUMBER_MAX_LENGTH,
            }),
            path: ["jobNumber"],
          });
        } else if (jobNumber === null || jobNumber <= 0 || !Number.isInteger(jobNumber)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.invalid", {
              label: tFn(`${TRANSLATION_KEY}.job-number`),
            }),
            path: ["jobNumber"],
          });
        }
      }

      const jobStartDate = parseDateValue(data.jobStartDate);
      const jobEndDate = parseDateValue(data.jobEndDate);
      const today = toStartOfDay(new Date());

      if (jobStartDate) {
        const normalizedJobStart = toStartOfDay(jobStartDate);
        if (normalizedJobStart.getTime() < today.getTime()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.must-not-be-in-past", {
              label: tFn(`${TRANSLATION_KEY}.job-start-date`),
            }),
            path: ["jobStartDate"],
          });
        }
      }

      if (jobEndDate) {
        const normalizedJobEnd = toStartOfDay(jobEndDate);
        if (normalizedJobEnd.getTime() < today.getTime()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.must-not-be-in-past", {
              label: tFn(`${TRANSLATION_KEY}.job-end-date`),
            }),
            path: ["jobEndDate"],
          });
        }

        if (jobStartDate && normalizedJobEnd.getTime() < toStartOfDay(jobStartDate).getTime()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.end-after-start", {
              label: tFn(`${TRANSLATION_KEY}.job-end-date`),
            }),
            path: ["jobEndDate"],
          });
        }

        if (
          truckType !== "TRUCK" &&
          gatepassExpiryDate &&
          normalizedJobEnd.getTime() > toStartOfDay(gatepassExpiryDate).getTime()
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.cant-be-after", {
              subject: tFn(`${TRANSLATION_KEY}.job-end-date`),
              object: tFn(`${TRANSLATION_KEY}.gatepass-duration`),
            }),
            path: ["jobEndDate"],
          });
        }
      }

      const seenPlateNumbers = new Set<string>();
      data.vehicles.forEach((vehicle, index) => {
        const normalizedPlate = vehicle.plateNumber.trim().toLowerCase();
        if (!normalizedPlate) return;

        if (seenPlateNumbers.has(normalizedPlate)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn(`${TRANSLATION_KEY}.duplicate-plate-number`),
            path: ["vehicles", index, "plateNumber"],
          });
          return;
        }

        seenPlateNumbers.add(normalizedPlate);
      });

      data.vehicles.forEach((vehicle, vehicleIndex) => {
        const firstDriverIndexByLicenseNumber = new Map<string, number>();

        (vehicle.drivers ?? []).forEach((driver, driverIndex) => {
          const hasPassportNumber = Boolean(driver.passportNumber?.trim());
          const hasEmiratesIdNumber = Boolean(driver.emiratesIdNumber?.trim());
          const driverLicenseNumber = String(driver.driverLicenseNumber ?? "")
            .trim()
            .toLowerCase();
          const isAutoPopulatedDriver =
            driverIndex === 0 && (isIndividualRole || vehicleIndex === 0);

          if (driverLicenseNumber) {
            const firstIndex = firstDriverIndexByLicenseNumber.get(driverLicenseNumber);
            if (firstIndex === undefined) {
              firstDriverIndexByLicenseNumber.set(driverLicenseNumber, driverIndex);
            } else {
              const message = tFn(`${TRANSLATION_KEY}.duplicate-driver-license-number`);
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message,
                path: ["vehicles", vehicleIndex, "drivers", firstIndex, "driverLicenseNumber"],
              });
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message,
                path: ["vehicles", vehicleIndex, "drivers", driverIndex, "driverLicenseNumber"],
              });
            }
          }

          if (hasPassportNumber) {
            const passportValidation = passportNumberSchema(
              15,
              tFn(`${TRANSLATION_KEY}.passport-number`),
            );
            const passportValue = String(driver.passportNumber ?? "").trim();
            const passportResult = passportValidation.safeParse(passportValue);
            if (!passportResult.success) {
              const message =
                passportResult.error.issues[0]?.message ||
                tFn("form-validation.invalid", {
                  label: tFn(`${TRANSLATION_KEY}.passport-number`),
                });
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message,
                path: ["vehicles", vehicleIndex, "drivers", driverIndex, "passportNumber"],
              });
            }
          }

          if (hasEmiratesIdNumber) {
            const emiratesValidation = emiratesIdNumberSchema(
              tFn(`${TRANSLATION_KEY}.emirates-id-number`),
            );
            const emiratesValue = String(driver.emiratesIdNumber ?? "").trim();
            const emiratesResult = emiratesValidation.safeParse(emiratesValue);
            if (!emiratesResult.success) {
              const message =
                emiratesResult.error.issues[0]?.message ||
                tFn("form-validation.invalid", {
                  label: tFn(`${TRANSLATION_KEY}.emirates-id-number`),
                });
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message,
                path: ["vehicles", vehicleIndex, "drivers", driverIndex, "emiratesIdNumber"],
              });
            }
          }

          if (!hasPassportNumber && !hasEmiratesIdNumber) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: tFn("form-validation.required", {
                label: tFn(`${TRANSLATION_KEY}.passport-number`),
              }),
              path: ["vehicles", vehicleIndex, "drivers", driverIndex, "passportNumber"],
            });

            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: tFn("form-validation.required", {
                label: tFn(`${TRANSLATION_KEY}.emirates-id-number`),
              }),
              path: ["vehicles", vehicleIndex, "drivers", driverIndex, "emiratesIdNumber"],
            });
          }

          if (
            !isAutoPopulatedDriver &&
            (hasPassportNumber || hasEmiratesIdNumber) &&
            !String(driver.securityClearanceId ?? "").trim()
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: tFn(`${TRANSLATION_KEY}.notifications.security-clearance-not-found`),
              path: ["vehicles", vehicleIndex, "drivers", driverIndex, "securityClearanceId"],
            });
          }

          if (
            gatepassExpiryExceedsScValidity(gatepassExpiryDate, driver.securityClearanceValidity)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: tFn(`${TRANSLATION_KEY}.gatepass-exceeds-security-clearance-expiry`),
              path: ["vehicles", vehicleIndex, "drivers", driverIndex, "securityClearanceValidity"],
            });
          }
        });
      });

      data.cargos.forEach((cargo, index) => {
        if (!cargo.cargoType?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.required", {
              label: tFn(`${TRANSLATION_KEY}.cargo-type`),
            }),
            path: ["cargos", index, "cargoType"],
          });
        }

        if (!cargo.cargo?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.required", {
              label: tFn(`${TRANSLATION_KEY}.cargo`),
            }),
            path: ["cargos", index, "cargo"],
          });
        }

        const quantity = parseNumeric(cargo.quantity);
        const hasQuantity = quantity !== null && quantity > 0;
        const hasUnit = Boolean(cargo.unitOfMeasurement?.trim());

        if (isActiveHazardCargo(cargo)) {
          if (hasQuantity || hasUnit) {
            if (!hasQuantity) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: tFn("form-validation.required", {
                  label: tFn(`${TRANSLATION_KEY}.quantity`),
                }),
                path: ["cargos", index, "quantity"],
              });
            } else if (decimalPlaces(cargo.quantity) > 4) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: tFn("form-validation.invalid", {
                  label: tFn(`${TRANSLATION_KEY}.quantity`),
                }),
                path: ["cargos", index, "quantity"],
              });
            }

            if (!hasUnit) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: tFn("form-validation.required", {
                  label: tFn(`${TRANSLATION_KEY}.unit-of-measurement`),
                }),
                path: ["cargos", index, "unitOfMeasurement"],
              });
            }
          }
        } else if (!isActiveHazardCargo(cargo) && (quantity === null || quantity <= 0)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.required", {
              label: tFn(`${TRANSLATION_KEY}.quantity`),
            }),
            path: ["cargos", index, "quantity"],
          });
        } else if (decimalPlaces(cargo.quantity) > 4) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.invalid", {
              label: tFn(`${TRANSLATION_KEY}.quantity`),
            }),
            path: ["cargos", index, "quantity"],
          });
        }

        if (!isActiveHazardCargo(cargo) && !cargo.unitOfMeasurement?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.required", {
              label: tFn(`${TRANSLATION_KEY}.unit-of-measurement`),
            }),
            path: ["cargos", index, "unitOfMeasurement"],
          });
        }
      });

      if (data.FOTTEntry === null || data.FOTTEntry === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn("form-validation.required", {
            label: tFn(`${TRANSLATION_KEY}.fott-entry`),
          }),
          path: ["FOTTEntry"],
        });
      }

      if (data.isFottDisabled && data.FOTTEntry !== false) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tFn(`${TRANSLATION_KEY}.fott-disabled-message`),
          path: ["FOTTEntry"],
        });
      }

      const parsedExpectedEntryDate = parseDateValue(data.expectedEntryDate);
      if (parsedExpectedEntryDate) {
        const selectedDate = toStartOfDay(parsedExpectedEntryDate);
        const today = toStartOfDay(new Date());

        if (selectedDate.getTime() < today.getTime()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tFn("form-validation.must-not-be-in-past", {
              label: tFn(`${TRANSLATION_KEY}.expected-entry-date`),
            }),
            path: ["expectedEntryDate"],
          });
        }
      }
    });
};

export type TruckGatepassFormData = z.infer<ReturnType<typeof getTruckGatepassFormSchema>>;
